import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Draggable from "react-draggable";
import { PhoneDisabled as EndCallIcon } from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

// GraphQL Operations
const GET_MIROTALK_MEETING = gql`
  mutation GetMiroTalkMeeting(
    $sessionId: ID!
    $role: String!
    $provider: String
  ) {
    getMiroTalkMeeting(
      sessionId: $sessionId
      role: $role
      provider: $provider
    ) {
      link
      roomName
      provider
      meetingToken
      meetingId
    }
  }
`;

const SEND_VIDEO_SIGNAL = gql`
  mutation SendVideoSignal($sessionId: ID!, $signal: JSON!, $sender: String!) {
    sendVideoSignal(sessionId: $sessionId, signal: $signal, sender: $sender)
  }
`;

const GET_PENDING_SIGNALS = gql`
  query GetPendingSignals($sessionId: ID!, $sender: String!) {
    getPendingVideoSignals(sessionId: $sessionId, sender: $sender)
  }
`;

const CLEAR_VIDEO_SIGNALS = gql`
  mutation ClearVideoSignals($sessionId: ID!) {
    clearVideoSignals(sessionId: $sessionId) {
      success
      clearedCount
      message
    }
  }
`;

// Styled components
const PopupContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  zIndex: 9999,
  width: "800px",
  height: "500px",
  backgroundColor: "#000",
  color: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  resize: "both !important",
  minWidth: "320px",
  minHeight: "240px",
  maxWidth: "90vw",
  maxHeight: "90vh",
  "&::after": {
    // Resize handle with better visibility
    content: '""',
    position: "absolute",
    bottom: 0,
    right: 0,
    width: "15px",
    height: "15px",
    cursor: "nwse-resize",
    backgroundImage:
      "linear-gradient(135deg, transparent 0%, transparent 50%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.5) 100%)",
  },
}));

const PopupHeader = styled(Box)(({ theme }) => ({
  padding: "10px",
  background: "linear-gradient(135deg, #4776E6, #8E54E9)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "move",
}));

const PopupContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#1a1a1a",
  overflow: "hidden",
  position: "relative",
}));

const ControlButton = styled(Button)(({ theme, active, danger }) => ({
  backgroundColor: danger
    ? "rgba(255,0,0,0.6)"
    : active
    ? "rgba(255,255,255,0.2)"
    : "rgba(255,0,0,0.3)",
  color: "white",
  minWidth: "40px",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  padding: 0,
  "&:hover": {
    backgroundColor: danger
      ? "rgba(255,0,0,0.8)"
      : active
      ? "rgba(255,255,255,0.3)"
      : "rgba(255,0,0,0.4)",
  },
}));

/**
 * MiroTalkVideoCallPopup Component - Floating popup version
 *
 * This component creates a draggable popup window for video calls
 * instead of embedding the video directly in the page.
 */
const MiroTalkVideoCallPopup = ({
  sessionId,
  callType = "video",
  onEndCall,
}) => {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const pollIntervalRef = useRef(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [showPopup, setShowPopup] = useState(true);
  const [initialUrl, setInitialUrl] = useState(null);
  const urlMonitorIntervalRef = useRef(null);
  const hasEndedRef = useRef(false); // Track if call has ended to prevent double cleanup

  // GraphQL mutations
  const [getMiroTalkMeeting] = useMutation(GET_MIROTALK_MEETING);
  const [sendVideoSignal] = useMutation(SEND_VIDEO_SIGNAL);
  const [clearVideoSignals] = useMutation(CLEAR_VIDEO_SIGNALS);

  // Query for pending signals
  const { refetch: refetchSignals } = useQuery(GET_PENDING_SIGNALS, {
    variables: { sessionId, sender: "agent" },
    skip: true, // Don't run this query automatically
  });

  // Reference to store logs between renders
  const logsRef = useRef([]);

  // Add to debug log
  const addLog = (message) => {
    const timestamp = new Date().toISOString().substring(11, 23);
    // Log to console immediately
    console.log(`[MiroTalkVideoCall] ${message}`);

    // Store in ref first to avoid render loops
    const newLog = `${timestamp}: ${message}`;
    logsRef.current = [newLog, ...logsRef.current].slice(0, 100);

    // Only update state when debug panel is visible to avoid unnecessary re-renders
    if (showDebug) {
      setDebugLogs(logsRef.current);
    }
  };

  // Initialize video call
  useEffect(() => {
    // Prevent initialization if no sessionId
    if (!sessionId) {
      return;
    }

    // Always reset hasEndedRef on new component mount
    hasEndedRef.current = false;
    console.log(
      `[MiroTalkVideoCall] Component mounting - hasEndedRef reset to false`
    );

    // Flag to prevent state updates after unmount
    let isActive = true;

    async function initializeCall() {
      try {
        addLog(`Initializing video call for session ${sessionId}`);
        if (isActive) setLoading(true);
        if (isActive) setError(null);

        // Get meeting link from backend
        const response = await getMiroTalkMeeting({
          variables: {
            sessionId,
            role: "agent",
            provider: "sfu",
          },
        });

        // Safe guard for unmounted component
        if (!isActive) return;

        if (response.errors) {
          throw new Error(response.errors[0].message);
        }

        const meetingData = response.data.getMiroTalkMeeting;
        if (isActive) setRoomInfo(meetingData);
        addLog(`Received room info: ${meetingData.roomName}`);

        // Send signal to notify of room creation
        await sendVideoSignal({
          variables: {
            sessionId,
            signal: {
              type: "mirotalk-room-created",
              roomName: meetingData.roomName,
              provider: meetingData.provider || "sfu",
            },
            sender: "agent",
          },
        });

        // Safe guard for unmounted component
        if (!isActive) return;

        // Start polling for signals - only if component still mounted
        if (isActive) await startPolling();

        // Wait a short time to ensure backend has registered the room
        setTimeout(() => {
          if (isActive) setLoading(false);
        }, 1000);
      } catch (err) {
        // Safe guard for unmounted component
        if (!isActive) return;

        addLog(`Error initializing call: ${err.message}`);
        setError(err.message);
        setLoading(false);
      }
    }

    initializeCall();

    // Cleanup function
    return () => {
      isActive = false;
      console.log(`[MiroTalkVideoCall] Component unmounting - cleaning up`);

      // Clear all intervals
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (urlMonitorIntervalRef.current) {
        clearInterval(urlMonitorIntervalRef.current);
        urlMonitorIntervalRef.current = null;
      }

      // Reset state to ensure fresh start next time
      setLoading(true);
      setConnected(false);
      setError(null);
      setRoomInfo(null);
      setShowPopup(true);
      setIsAudioEnabled(true);
      setIsVideoEnabled(true);
      setDebugLogs([]);
      logsRef.current = [];
      hasEndedRef.current = false;
    };
  }, [sessionId]);

  // Track if component is mounted
  const isMountedRef = useRef(true);

  // Set mounted state on component mount/unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Start polling for signals
  const startPolling = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Clear any stale signals before starting polling to prevent old "call-ended" signals
    try {
      addLog("Clearing stale video signals before starting polling...");
      const clearResult = await clearVideoSignals({
        variables: { sessionId },
      });

      if (clearResult.data?.clearVideoSignals?.success) {
        const { clearedCount, message } = clearResult.data.clearVideoSignals;
        addLog(
          `Successfully cleared ${clearedCount} stale signals: ${message}`
        );
      } else {
        addLog("Signal cleanup completed (no result data)");
      }
    } catch (err) {
      addLog(
        `Warning: Could not clear stale signals: ${err.message} - continuing with polling`
      );
    }

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      // Don't poll if component is no longer mounted
      if (!isMountedRef.current) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }

      try {
        const result = await refetchSignals();

        // Check if still mounted after async operation
        if (!isMountedRef.current) return;

        if (result.data && result.data.getPendingVideoSignals) {
          const signals = result.data.getPendingVideoSignals;

          if (signals.length > 0) {
            addLog(`Received ${signals.length} signals`);

            // Create a batch of state updates to avoid multiple renders
            let shouldUpdateConnected = false;
            let newConnectedState = false;
            let shouldUpdateAudio = false;
            let newAudioState = false;
            let shouldUpdateVideo = false;
            let newVideoState = false;
            let shouldEndCall = false;

            // Process signals
            signals.forEach((signal) => {
              if (signal.type === "join-call") {
                addLog("User joined the call");
                shouldUpdateConnected = true;
                newConnectedState = true;
              } else if (signal.type === "call-ended") {
                addLog("User ended the call");
                shouldUpdateConnected = true;
                newConnectedState = false;
                shouldEndCall = true;
              } else if (signal.type === "audio-muted") {
                shouldUpdateAudio = true;
                newAudioState = false;
              } else if (signal.type === "audio-unmuted") {
                shouldUpdateAudio = true;
                newAudioState = true;
              } else if (signal.type === "video-muted") {
                shouldUpdateVideo = true;
                newVideoState = false;
              } else if (signal.type === "video-unmuted") {
                shouldUpdateVideo = true;
                newVideoState = true;
              }
            });

            // Apply state updates in batch
            if (!isMountedRef.current) return;

            if (shouldUpdateConnected) {
              setConnected(newConnectedState);
            }

            if (shouldUpdateAudio) {
              setIsAudioEnabled(newAudioState);
            }

            if (shouldUpdateVideo) {
              setIsVideoEnabled(newVideoState);
            }

            // Handle end call separately with a small delay
            if (shouldEndCall && onEndCall && isMountedRef.current) {
              // Stop polling to avoid any race conditions
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }

              // Use timeout to ensure all state updates complete
              setTimeout(() => {
                if (isMountedRef.current && onEndCall) {
                  onEndCall();
                }
              }, 100);
            }
          }
        }
      } catch (err) {
        if (isMountedRef.current) {
          addLog(`Error polling signals: ${err.message}`);
        }
      }
    }, 2000);
  };

  // End the call - use a useCallback to avoid unnecessary function recreations
  const handleEndCall = React.useCallback(async () => {
    // Check if component is mounted and not already ended
    if (!isMountedRef.current || hasEndedRef.current) {
      console.log(
        "[MiroTalkVideoCall] Call already ended or component unmounted"
      );
      return;
    }

    // Mark as ended to prevent double cleanup
    hasEndedRef.current = true;

    try {
      addLog("Ending call - starting cleanup");

      // First trigger iframe's leaveRoomBtn if available
      try {
        const leaveBtn =
          iframeRef.current?.contentWindow?.document?.getElementById(
            "leaveRoomBtn"
          );
        if (leaveBtn) {
          leaveBtn.click(); // Trigger iframe's end call
          addLog("Triggered MiroTalk leave button");
        } else {
          addLog("MiroTalk leaveRoomBtn not found for triggering");
        }
      } catch (err) {
        addLog(
          `Cannot trigger iframe button due to cross-origin restrictions: ${err.message}`
        );
      }

      // Then stop polling and URL monitoring to prevent potential race conditions
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      if (urlMonitorIntervalRef.current) {
        clearInterval(urlMonitorIntervalRef.current);
        urlMonitorIntervalRef.current = null;
      }

      // Set as disconnected immediately
      setConnected(false);

      // Send end call signal
      await sendVideoSignal({
        variables: {
          sessionId,
          signal: { type: "call-ended" },
          sender: "agent",
        },
      }).catch((err) => {
        console.error("Failed to send end call signal:", err);
      });

      // Check if still mounted after async operation
      if (!isMountedRef.current) return;

      // Try to send leave room message to iframe
      if (iframeRef.current?.contentWindow) {
        try {
          iframeRef.current.contentWindow.postMessage(
            {
              action: "leaveRoom",
            },
            "*"
          );
          addLog("Sent leaveRoom message to iframe");
        } catch (e) {
          addLog("Error sending leaveRoom message: " + e.message);
        }
      }

      // Reset iframe src to about:blank to force cleanup
      if (iframeRef.current) {
        iframeRef.current.src = "about:blank";
      }

      // Close the popup
      setShowPopup(false);

      // Use a timeout to ensure all state updates complete before ending call
      setTimeout(() => {
        // Notify parent component
        if (isMountedRef.current && onEndCall) {
          onEndCall();
        }
      }, 100);
    } catch (err) {
      if (isMountedRef.current) {
        addLog(`Error ending call: ${err.message}`);
      }

      // Even if error occurs, still try to notify parent
      if (isMountedRef.current && onEndCall) {
        setTimeout(() => {
          if (isMountedRef.current && onEndCall) {
            onEndCall();
          }
        }, 100);
      }
    }
  }, [sessionId, onEndCall]);

  // Toggle audio
  const toggleAudio = async () => {
    try {
      const newState = !isAudioEnabled;
      setIsAudioEnabled(newState);

      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            action: newState ? "unmuteAudio" : "muteAudio",
          },
          "*"
        );

        addLog(`Toggled audio: ${newState ? "on" : "off"}`);
      }
    } catch (err) {
      addLog(`Error toggling audio: ${err.message}`);
    }
  };

  // Toggle video
  const toggleVideo = async () => {
    try {
      const newState = !isVideoEnabled;
      setIsVideoEnabled(newState);

      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            action: newState ? "unmuteVideo" : "muteVideo",
          },
          "*"
        );

        addLog(`Toggled video: ${newState ? "on" : "off"}`);
      }
    } catch (err) {
      addLog(`Error toggling video: ${err.message}`);
    }
  };

  // Get dynamic video call host based on current environment
  const getVideoCallHost = () => {
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;
    console.log("🔍 DEBUG (React agent): Current hostname:", currentHost);
    console.log("🔍 DEBUG (React agent): Current protocol:", currentProtocol);

    if (currentHost.includes("laptop")) {
      const laptopHost = "https://laptop:3010";
      console.log(
        "🔍 DEBUG (React agent): Using laptop host (HTTPS):",
        laptopHost
      );
      return laptopHost;
    }

    console.log(
      "🔍 DEBUG (React agent): Using production host: https://sfu.instaconnect.io"
    );
    return "https://sfu.instaconnect.io"; // Default production host
  };

  // Get MiroTalk URL with all parameters for auto-join
  const getMiroTalkUrl = () => {
    if (!roomInfo || !roomInfo.roomName) return "";

    try {
      const serverUrl = getVideoCallHost();
      const roomName = roomInfo.roomName;
      const displayName = `agent_${Math.random().toString(36).substring(2, 8)}`;
      const videoEnabled = callType === "video";

      const params = new URLSearchParams({
        room: roomName,
        name: displayName,
        audio: "true",
        video: videoEnabled ? "true" : "false",
        screen: "false",
        hide: "true",
        notify: "false",
        join: "true",
        chat: "false",
        autoConnect: "true",
        ts: Date.now(),
      });

      // Preserve the meeting access token from backend
      if (roomInfo.meetingToken) {
        params.set("icToken", roomInfo.meetingToken);
      }

      const url = `${serverUrl}/join?${params.toString()}`;
      addLog(`Created MiroTalk URL (${callType} call): ${url}`);
      return url;
    } catch (err) {
      addLog(`Error creating MiroTalk URL: ${err.message}`);
      const videoEnabled = callType === "video";
      let fallback = `${getVideoCallHost()}/join?room=${
        roomInfo.roomName
      }&name=agent&audio=true&video=${videoEnabled}&screen=false&hide=false&notify=false&join=true`;
      if (roomInfo.meetingToken) {
        fallback += `&icToken=${encodeURIComponent(roomInfo.meetingToken)}`;
      }
      return fallback;
    }
  };

  // Handle iframe messages - only setup once and use ref for showDebug
  const showDebugRef = useRef(showDebug);

  // Keep ref in sync with state
  useEffect(() => {
    showDebugRef.current = showDebug;
  }, [showDebug]);

  // Setup message handler once
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle MiroTalk button click events first (bypass iframe source check)
      if (
        event.data &&
        event.data.type === "mirotalk_button_clicked" &&
        event.data.button === "leaveRoomBtn"
      ) {
        console.log("📹 Agent side: leaveRoomBtn clicked in MiroTalk!");
        alert("🚨 DEBUG: leaveRoomBtn clicked! Ending video call...");
        handleEndCall();
        return;
      }

      // Handle MiroTalk leave room event
      if (event.data && event.data.type === "mirotalk_leave_room") {
        console.log("📹 Agent side: MiroTalk leave room triggered!");
        alert("🚨 DEBUG: MiroTalk leave room triggered!");
        handleEndCall();
        return;
      }

      // Only process messages from our iframe
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      // Make sure component is still mounted
      if (!isMountedRef.current) return;

      try {
        // Use the ref instead of the state to avoid recreating the handler
        if (showDebugRef.current) {
          addLog(`Received message from iframe: ${JSON.stringify(event.data)}`);
        }

        // Handle various message types
        if (
          event.data.action === "roomReady" ||
          event.data.action === "userJoined"
        ) {
          setConnected(true);
          setLoading(false);
        } else if (event.data.action === "audioMuted") {
          setIsAudioEnabled(false);
        } else if (event.data.action === "audioUnmuted") {
          setIsAudioEnabled(true);
        } else if (event.data.action === "videoMuted") {
          setIsVideoEnabled(false);
        } else if (event.data.action === "videoUnmuted") {
          setIsVideoEnabled(true);
        }
      } catch (err) {
        if (isMountedRef.current) {
          addLog(`Error handling iframe message: ${err.message}`);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Empty dependency array - only run once

  // Monitor iframe for error pages only (minimal monitoring)
  useEffect(() => {
    if (!roomInfo || !iframeRef.current) return;

    // Set initial URL for reference only
    const initialVideoUrl = getMiroTalkUrl();
    setInitialUrl(initialVideoUrl);
    addLog(`Initial video URL set: ${initialVideoUrl}`);

    // Only monitor for error pages, not URL changes
    const startErrorMonitoring = () => {
      if (urlMonitorIntervalRef.current) {
        clearInterval(urlMonitorIntervalRef.current);
      }

      urlMonitorIntervalRef.current = setInterval(() => {
        if (!isMountedRef.current || !iframeRef.current) {
          if (urlMonitorIntervalRef.current) {
            clearInterval(urlMonitorIntervalRef.current);
            urlMonitorIntervalRef.current = null;
          }
          return;
        }

        try {
          // Only check for error pages in iframe
          const iframeDoc = iframeRef.current.contentDocument;
          if (
            iframeDoc &&
            iframeDoc.title &&
            (iframeDoc.title.includes("Error") ||
              iframeDoc.title.includes("404"))
          ) {
            addLog(`Iframe shows error page: ${iframeDoc.title} - ending call`);
            handleEndCall();
          }
        } catch (error) {
          // Expected cross-origin error - silently continue monitoring
        }
      }, 5000); // Check every 5 seconds (reduced frequency)
    };

    // Wait for iframe to load before starting monitoring
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.onload = () => {
        setTimeout(startErrorMonitoring, 2000); // Give iframe time to fully load
      };

      // If iframe is already loaded, start monitoring immediately
      if (iframe.readyState === "complete") {
        setTimeout(startErrorMonitoring, 2000);
      }
    }

    return () => {
      if (urlMonitorIntervalRef.current) {
        clearInterval(urlMonitorIntervalRef.current);
        urlMonitorIntervalRef.current = null;
      }
    };
  }, [roomInfo]); // Remove handleEndCall dependency to prevent re-runs

  // Monitor iframe's internal leaveRoomBtn
  useEffect(() => {
    if (!roomInfo || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const setupIframeButtonMonitoring = () => {
      setTimeout(() => {
        if (!isMountedRef.current) return;

        try {
          const leaveBtn =
            iframe.contentWindow?.document?.getElementById("leaveRoomBtn");
          if (leaveBtn) {
            leaveBtn.addEventListener("click", () => {
              console.log("📹 MiroTalk leave button clicked - ending call...");
              if (isMountedRef.current) {
                handleEndCall(); // Trigger our bilateral end call
              }
            });
            addLog("Successfully attached listener to MiroTalk leaveRoomBtn");
          } else {
            addLog("MiroTalk leaveRoomBtn not found in iframe");
          }
        } catch (err) {
          addLog(
            `Cross-origin restriction - cannot access iframe buttons: ${err.message}`
          );
        }
      }, 2000); // Wait 2 seconds for MiroTalk to fully load
    };

    // Setup monitoring when iframe loads
    iframe.onload = () => {
      setupIframeButtonMonitoring();
    };

    // If iframe is already loaded, setup monitoring immediately
    if (iframe.readyState === "complete") {
      setupIframeButtonMonitoring();
    }
  }, [roomInfo]); // Remove handleEndCall dependency to prevent re-runs

  // Hide UI elements in MiroTalk iframe
  const hideIframeUiElements = () => {
    if (!iframeRef.current || !iframeRef.current.contentWindow) return;

    try {
      // Send messages to iframe to hide elements
      const messagesToSend = [
        { action: "hideRoomName" },
        { action: "hideChat" },
        { action: "hideHeader" },
        { action: "hideSettings" },
        { action: "hideVideoQualitySelector" },
      ];

      messagesToSend.forEach((msg) => {
        iframeRef.current.contentWindow.postMessage(msg, "*");
      });

      addLog("Sent messages to hide UI elements");
    } catch (err) {
      addLog(`Error hiding iframe UI elements: ${err.message}`);
    }
  };

  // Periodically hide UI elements - use ref to avoid dependency
  const roomInfoRef = useRef(roomInfo);

  // Keep ref in sync with state
  useEffect(() => {
    roomInfoRef.current = roomInfo;
  }, [roomInfo]);

  // Setup UI hiding once after roomInfo is set
  useEffect(() => {
    // Don't start if there's no roomInfo yet
    if (!roomInfoRef.current) return;

    // Initial hide
    const initialTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        hideIframeUiElements();
      }
    }, 2000);

    // Periodically hide elements (sometimes they can reappear after MiroTalk updates UI)
    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
        hideIframeUiElements();
      }
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [roomInfo]); // Only re-run when roomInfo changes

  // Don't render anything if popup is hidden
  if (!showPopup) {
    return null;
  }

  // Handle drag stop
  const handleDragStop = (e, data) => {
    setPosition({ x: data.x, y: data.y });
  };

  // Render error message
  if (error) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: "20%",
          left: "20%",
          zIndex: 9999,
          width: "400px",
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error: {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            setShowPopup(false);
            if (onEndCall) onEndCall();
          }}
          sx={{ mt: 2 }}
        >
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Draggable
      handle=".drag-handle"
      defaultPosition={position}
      onStop={handleDragStop}
      bounds="body"
    >
      <PopupContainer style={{ resize: "both" }}>
        {/* Popup header */}
        <PopupHeader className="drag-handle">
          <Box display="flex" alignItems="center">
            <DragIndicatorIcon sx={{ mr: 1 }} />
            <Typography variant="body1" fontWeight="bold">
              {callType === "audio" ? "Audio" : "Video"} Call with Client
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              onClick={handleEndCall}
              startIcon={<EndCallIcon />}
              sx={{
                backgroundColor: "rgba(220, 53, 69, 0.8)",
                color: "white",
                fontSize: "12px",
                padding: "4px 8px",
                minWidth: "80px",
                "&:hover": { backgroundColor: "rgba(220, 53, 69, 1)" },
              }}
            >
              End Call
            </Button>
            <Button
              onClick={handleEndCall}
              sx={{
                minWidth: "30px",
                p: 0,
                color: "white",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </PopupHeader>

        {/* Popup content */}
        <PopupContent>
          {/* MiroTalk iframe */}
          {roomInfo && !loading ? (
            <iframe
              ref={iframeRef}
              src={getMiroTalkUrl()}
              allow="camera; microphone; display-capture; fullscreen; clipboard-read; clipboard-write; autoplay"
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Video Call"
            />
          ) : null}

          {/* Loading overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                zIndex: 5,
              }}
            >
              <CircularProgress color="inherit" size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Connecting to video call...
              </Typography>
            </Box>
          )}

          {/* Debug toggle button */}
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setShowDebug((prev) => {
                // When toggling debug on, update logs from ref
                if (!prev) {
                  setDebugLogs(logsRef.current);
                }
                return !prev;
              });
            }}
            sx={{
              position: "absolute",
              top: "5px",
              right: "5px",
              minWidth: "30px",
              width: "30px",
              height: "30px",
              padding: 0,
              zIndex: 30,
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          >
            <BugReportIcon sx={{ fontSize: 16 }} />
          </Button>

          {/* Debug panel */}
          {showDebug && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "250px",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "#ffffff",
                padding: 1,
                overflow: "auto",
                zIndex: 20,
                fontSize: "12px",
                fontFamily: "monospace",
              }}
            >
              <Typography variant="body2" component="div">
                Debug Info:
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="caption" component="div">
                  Session ID: {sessionId}
                </Typography>
                <Typography variant="caption" component="div">
                  Room: {roomInfo?.roomName || "Not created"}
                </Typography>
                <Typography variant="caption" component="div">
                  Status:{" "}
                  {loading
                    ? "Loading"
                    : connected
                    ? "Connected"
                    : "Disconnected"}
                </Typography>
              </Box>
              <Typography variant="body2" component="div">
                Log:
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  maxHeight: "calc(100% - 120px)",
                  overflow: "auto",
                }}
              >
                {debugLogs.map((log, idx) => (
                  <pre key={idx} style={{ margin: 0, color: "#ffff80" }}>
                    {log}
                  </pre>
                ))}
              </Box>
            </Box>
          )}
        </PopupContent>
      </PopupContainer>
    </Draggable>
  );
};

export default MiroTalkVideoCallPopup;
